import accessCodeService from '../services/accessCodeService';

// Valid access codes for demo access (legacy - kept for fallback)
// These are now managed via Supabase database, but kept here as fallback
export const ACCESS_CODES = [
  { code: 'adminwisdomain' }, // Permanent access, unlimited valuations
  { code: 'IPBCASIA2025', expiresAt: '2025-12-20' }, 
  { code: 'MooreTechLaw', expiresAt: '2025-12-12', maxValuations: 3 }, 
];

// Cache for access code validation to reduce database calls
const validationCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Helper function to check if an access code is valid and not expired
// Now uses Supabase service with caching and fallback to hardcoded codes
export const validateAccessCode = async (inputCode) => {
  if (!inputCode || !inputCode.trim()) {
    return { valid: false, expired: false };
  }

  const lowerInputCode = inputCode.toLowerCase().trim();
  
  // Check cache first
  const cacheKey = `validate_${lowerInputCode}`;
  const cached = validationCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.result;
  }

  try {
    // Try Supabase service first
    const result = await accessCodeService.validateAccessCode(inputCode);
    
    // Cache the result
    validationCache.set(cacheKey, {
      result,
      timestamp: Date.now(),
    });
    
    return result;
  } catch (error) {
    console.warn('Error validating access code via service, falling back to hardcoded codes:', error);
    
    // Fallback to hardcoded validation
    for (const accessCode of ACCESS_CODES) {
      const code = typeof accessCode === 'string' ? accessCode : accessCode.code;
      const lowerCode = code.toLowerCase();
      
      if (lowerCode === lowerInputCode) {
        // Check if code has expiration date
        if (typeof accessCode === 'object' && accessCode.expiresAt) {
          const expirationDate = new Date(accessCode.expiresAt);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (expirationDate < today) {
            return { valid: false, expired: true, expirationDate: accessCode.expiresAt };
          }
        }
        
        return { 
          valid: true, 
          expired: false, 
          code: code,
          maxValuations: typeof accessCode === 'object' ? accessCode.maxValuations : undefined
        };
      }
    }
    
    return { valid: false, expired: false };
  }
};

// Get all valid (non-expired) access codes for backward compatibility
// This is now async and fetches from Supabase
export const getValidAccessCodes = async () => {
  try {
    const codes = await accessCodeService.getAllAccessCodes({ includeInactive: false });
    return codes.map(ac => ac.code);
  } catch (error) {
    console.warn('Error fetching valid access codes, using fallback:', error);
    // Fallback to hardcoded codes
    return ACCESS_CODES
      .filter(ac => {
        const accessCode = typeof ac === 'string' ? { code: ac } : ac;
        if (accessCode.expiresAt) {
          const expirationDate = new Date(accessCode.expiresAt);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return expirationDate >= today;
        }
        return true;
      })
      .map(ac => typeof ac === 'string' ? ac : ac.code);
  }
};

// Legacy export for backward compatibility (synchronous, uses hardcoded codes)
export const VALID_ACCESS_CODES = ACCESS_CODES
  .filter(ac => {
    const accessCode = typeof ac === 'string' ? { code: ac } : ac;
    if (accessCode.expiresAt) {
      const expirationDate = new Date(accessCode.expiresAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return expirationDate >= today;
    }
    return true;
  })
  .map(ac => typeof ac === 'string' ? ac : ac.code);

// Utility functions for tracking valuation usage
// Now uses Supabase service with localStorage fallback
const getUsageStorageKey = (code) => `demo_valuation_usage_${code.toLowerCase()}`;

// Get current usage count for an access code
export const getValuationUsage = async (code) => {
  try {
    return await accessCodeService.getValuationUsage(code);
  } catch (error) {
    console.warn('Error getting valuation usage, falling back to localStorage:', error);
    // Fallback to localStorage
    try {
      const stored = localStorage.getItem(getUsageStorageKey(code));
      return stored ? parseInt(stored, 10) : 0;
    } catch (localError) {
      console.warn('Failed to read valuation usage from localStorage:', localError);
      return 0;
    }
  }
};

// Increment usage count for an access code
export const incrementValuationUsage = async (code, metadata = {}) => {
  try {
    return await accessCodeService.incrementValuationUsage(code, metadata);
  } catch (error) {
    console.warn('Error incrementing valuation usage, falling back to localStorage:', error);
    // Fallback to localStorage
    try {
      const current = await getValuationUsage(code);
      const newCount = current + 1;
      localStorage.setItem(getUsageStorageKey(code), newCount.toString());
      return newCount;
    } catch (localError) {
      console.error('Failed to save valuation usage to localStorage:', localError);
      throw localError;
    }
  }
};

// Check if access code has reached its valuation limit
export const checkValuationLimit = async (code, maxValuations) => {
  if (!maxValuations) {
    return { allowed: true, remaining: null }; // Unlimited
  }
  
  const currentUsage = await getValuationUsage(code);
  const remaining = Math.max(0, maxValuations - currentUsage);
  
  return {
    allowed: currentUsage < maxValuations,
    remaining: remaining,
    used: currentUsage,
    limit: maxValuations
  };
};

